import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  canUpgradePlan,
  getCurrentUser,
  getTenantPlanName,
  hasTenantLevelClusterGroup,
  isSaaS,
} from "state/auth/selectors";
import styled from "styled-components";
import {
  red,
  green,
  darkBlue,
  warning,
  success,
  white,
} from "utils/constants/colors";
import { FLAGS } from "utils/constants/flags";
import flags from "services/flags";
import permissionsService from "services/permissions";

import { pendingCancellationModal } from "state/plandetails/services";
import { isSubscriptionInPending } from "state/plandetails/selectors";
import PendingCancellationModal from "pages/settings/plandetails/components/PendingCancellationModal";
import { useMenu } from "components/common/providers/Menu";
import { TextButton } from "components/ui/Button";
import Icon from "components/ui/Icon";
import { faArrowCircleUp } from "@fortawesome/free-solid-svg-icons";
import {
  faEngineWarning,
  faInfoCircle,
  faOctagonExclamation,
  faQuestionCircle,
  faWarning,
} from "@fortawesome/pro-light-svg-icons";
import { Tooltip } from "antd";
import { getSystemActivationDetails } from "state/activation/selectors";
import appEnv from "services/app";

const STATUS_COLOR_MAPPING = {
  warning,
  active: success,
  inactive: red,
};

const Wrapper = styled.div`
  background: rgba(0, 0, 0, 0.2);
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 4px 9px;
  color: #ddf4f3;
  text-overflow: ellipsis;
  text-align: center;

  &:empty {
    min-height: 0;
    padding: 0;
  }
`;

const UpgradeContainer = styled(TextButton)`
  flex-grow: 1;
  background-color: ${(props) => (props.warning ? red : green)};
  color: #3e4856;
  margin: 15px 0 10px 0;
  display: inline-block;
  line-height: 32px;
  text-transform: none;

  &:focus,
  &:hover {
    background-color: ${(props) => (props.warning ? red : green)};
    color: #3e4856;
  }
`;

const InfoText = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #bcc3cd;
  white-space: normal;
  text-transform: none;
  margin-top: 8px;
  width: 176px;

  > h3 {
    color: #bcc3cd;
  }
`;

const SystemPlanIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 16px;
  width: 32px;
  height: 32px;
  border-radius: 2px;

  background: ${(props) => STATUS_COLOR_MAPPING[props.status]};

    color: ${white};
    font-size: 16px;
  }
`;

const SystemPlanWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-size: 12px;
  padding: 8px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  & svg {
    color: ${(props) => STATUS_COLOR_MAPPING[props.status]};
  }

  & > span {
    text-transform: uppercase;
    color: ${(props) => STATUS_COLOR_MAPPING[props.status]};
  }
`;

const StateWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > span {
    color: #9a97ae;
    font-weight: 400;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
`;

function OnPremPlanDetails({ expanded, activationDetails }) {
  const { t } = useTranslation();
  const isVertexEnv = appEnv?.theme?.isVertex;
  const productType = isVertexEnv ? "Vertex" : "Palette";
  const tooltipProps = {
    title: t(
      "Contact your system administrator to activate your {{productType}} instance",
      { productType }
    ),
    placement: "right",
  };

  if (activationDetails?.state === "inactive") {
    return expanded ? (
      <SystemPlanWrap status="inactive">
        <span>{t("Trial period expired")}</span>
        <Divider />
        <StateWrap>
          <span>{t("Read - only mode")}</span>
          <Tooltip {...tooltipProps}>
            <Icon awesome={faOctagonExclamation} />
          </Tooltip>
        </StateWrap>
      </SystemPlanWrap>
    ) : (
      <Tooltip
        title={t(
          "Trial period expired, contact your system administrator to activate your {{productType}} instance",
          { productType }
        )}
        placement="right"
      >
        <SystemPlanIconWrap status="inactive">
          <Icon awesome={faOctagonExclamation} />
        </SystemPlanIconWrap>
      </Tooltip>
    );
  }

  if (
    activationDetails?.state === "pending" &&
    activationDetails.isAboutToExpire
  ) {
    return expanded ? (
      <SystemPlanWrap status="warning">
        <span>{t("Trial period about to expire")}</span>
        <Divider />
        <StateWrap>
          <span>
            {!activationDetails.daysRemaining
              ? t("Less than a day left")
              : t("{{daysLeft}} days left", {
                  daysLeft: activationDetails.daysRemaining,
                })}
          </span>
          <Tooltip {...tooltipProps}>
            <Icon awesome={faQuestionCircle} />
          </Tooltip>
        </StateWrap>
      </SystemPlanWrap>
    ) : (
      <Tooltip
        title={t(
          "Trial period about to expire, contact your system administrator to activate your {{productType}} instance",
          { productType }
        )}
        placement="right"
      >
        <SystemPlanIconWrap status="warning">
          <Icon awesome={faWarning} />
        </SystemPlanIconWrap>
      </Tooltip>
    );
  }

  if (activationDetails?.state === "pending") {
    return expanded ? (
      <SystemPlanWrap status="active">
        <span>{t("Trial period")}</span>
      </SystemPlanWrap>
    ) : (
      <Tooltip title={t("Trial period")} placement="right">
        <SystemPlanIconWrap status="active">
          <Icon awesome={faInfoCircle} />
        </SystemPlanIconWrap>
      </Tooltip>
    );
  }

  return null;
}

function PlanContainer({
  planName,
  canUpgrade,
  pendingCancellation,
  devMode,
  hasTenantLevelUsage,
  isSaaS,
  activationDetails,
}) {
  const { t } = useTranslation();
  const { expanded } = useMenu();

  if (!isSaaS && activationDetails && !devMode) {
    return (
      <OnPremPlanDetails
        expanded={expanded}
        activationDetails={activationDetails}
      />
    );
  }

  if (!planName || !flags.has(FLAGS.BILLING) || !permissionsService.isAdmin) {
    return null;
  }

  const renderUpgradeContainer = () => {
    if (devMode && !hasTenantLevelUsage) {
      return (
        <>
          {expanded ? (
            <>
              <InfoText>
                <h3>{t("Want more quota?")}</h3>
              </InfoText>
              <InfoText>{t("Let us know and we'll get back to you.")}</InfoText>
            </>
          ) : null}
          <Tooltip
            title={expanded ? null : t("Get more quota")}
            placement="right"
            color={darkBlue}
          >
            <UpgradeContainer
              as="a"
              warning={false}
              expanded={expanded}
              href={`mailto:developer@spectrocloud.com?subject=Palette user quota request &body=Hello Spectro Cloud,%0A%0AI have been using Palette App Mode and would like additional quota because of my use case <tell us more>.%0A%0ALooking forward to your response.`}
            >
              {!expanded ? (
                <Icon awesome={faArrowCircleUp} />
              ) : (
                t("Get more quota")
              )}
            </UpgradeContainer>
          </Tooltip>
        </>
      );
    }

    if (!devMode) {
      if (pendingCancellation) {
        return (
          <>
            {expanded ? (
              <InfoText>
                {t("We are sorry to see you go! Please migrate your clusters")}
              </InfoText>
            ) : null}
            <Tooltip
              title={expanded ? null : t("Plan cancellation pending")}
              placement="right"
              color={darkBlue}
            >
              <UpgradeContainer
                onClick={() => pendingCancellationModal.open()}
                warning={true}
                expanded={expanded}
              >
                {!expanded ? <Icon awesome={faEngineWarning} /> : t("Details")}
              </UpgradeContainer>
            </Tooltip>
          </>
        );
      }

      if (canUpgrade) {
        return (
          <>
            {expanded ? (
              <InfoText>
                {t("Start small and only pay for what you use!")}
              </InfoText>
            ) : null}
            <Tooltip
              title={expanded ? null : t("Upgrade now")}
              placement="right"
              color={darkBlue}
            >
              <UpgradeContainer
                as={Link}
                to="/admin/settings/plandetails"
                warning={pendingCancellation}
                expanded={expanded}
              >
                {!expanded ? (
                  <Icon awesome={faArrowCircleUp} />
                ) : (
                  t("Upgrade now")
                )}
              </UpgradeContainer>
            </Tooltip>
          </>
        );
      }

      return planName();
    }
  };

  return (
    <Wrapper>
      {renderUpgradeContainer()}
      <PendingCancellationModal />
    </Wrapper>
  );
}

export default connect((state) => ({
  planName: getTenantPlanName(state),
  canUpgrade: canUpgradePlan(state),
  pendingCancellation: isSubscriptionInPending(state),
  devMode: state.auth.devMode,
  currentUser: getCurrentUser(state),
  hasTenantLevelUsage: hasTenantLevelClusterGroup(state),
  isSaaS: isSaaS(state),
  activationDetails: getSystemActivationDetails(state),
}))(PlanContainer);
